import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, ShieldAlert, Lock, Send } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../lib/auth";

const REASON_OPTIONS = [
  ["business_closing", "Business closing"],
  ["fleet_sold", "Fleet being sold"],
  ["no_longer_renting", "No longer offering rentals"],
  ["moving_away", "Moving away from ConfirmPH"],
  ["enabled_accidentally", "Enabled accidentally"],
  ["operational_requirements_changed", "Operational requirements changed"],
  ["other", "Other"],
];

function ActivationModal({ onClose, onActivated }) {
  const [ack1, setAck1] = useState(false);
  const [ack2, setAck2] = useState(false);
  const [busy, setBusy] = useState(false);
  const [precheck, setPrecheck] = useState(null);
  const canEnable = ack1 && ack2 && !busy;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/enhanced-safety/activation-precheck");
        if (!cancelled) setPrecheck(data);
      } catch (_) { /* informational only - never blocks activation */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const enable = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/enhanced-safety/activate", {
        confirm: "ENABLE",
        acknowledge_cannot_disable: ack1,
        acknowledge_holds_cannot_be_bypassed: ack2,
      });
      toast.success("Enhanced Safety Enforcement is now active.");
      onActivated(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4" data-testid="enhanced-safety-activate-modal">
      <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4">
        <h2 className="font-display text-xl font-semibold">Enable Enhanced Vehicle Safety Enforcement?</h2>
        <p className="text-sm text-slate-600">
          When enabled, serious vehicle safety, document, inspection, identity, or integrity issues may
          prevent affected vehicles from completing ConfirmPH rental handover until the issue is resolved.
        </p>
        <p className="text-sm text-slate-600 font-medium">
          Once enabled, you and your staff cannot turn this feature off directly. If you later need Enhanced
          Safety Enforcement disabled, the registered business owner must submit a deactivation request to
          ConfirmPH for review.
        </p>
        <p className="text-xs text-slate-500">
          Businesses using Enhanced Safety Enforcement may receive higher trust positioning in ConfirmPH
          rental discovery, since they've accepted stronger vehicle-safety controls. If Enhanced Safety is
          not enabled, your business may appear after participating businesses in applicable listings.{" "}
          <Link to="/enhanced-safety" target="_blank" className="underline">Learn more</Link>
        </p>
        {precheck && (
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="activation-precheck">
            <div className="font-medium text-slate-800">
              Fleet checked: {precheck.vehicles_checked} vehicles · Potential issues: {precheck.potential_issues}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              This means ConfirmPH found no currently known blocking condition under the configured Enhanced
              Safety rules for {precheck.vehicles_checked - precheck.potential_issues} of your vehicles - not a
              mechanical inspection, government certification, or safety guarantee.
            </p>
            {precheck.potential_issues > 0 && (
              <ul className="mt-2 space-y-1 text-xs text-amber-800">
                {precheck.issues.slice(0, 5).map((iss, i) => (
                  <li key={i}>• {iss.vehicle_name}: {iss.message}</li>
                ))}
                {precheck.issues.length > 5 && <li>…and {precheck.issues.length - 5} more</li>}
              </ul>
            )}
          </div>
        )}
        <div className="space-y-2 border-t border-slate-100 pt-4">
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={ack1} onChange={(e) => setAck1(e.target.checked)}
                   data-testid="ack-cannot-disable" className="mt-0.5" />
            I understand that my business cannot disable Enhanced Safety Enforcement itself after activation.
          </label>
          <label className="flex items-start gap-2 text-sm">
            <input type="checkbox" checked={ack2} onChange={(e) => setAck2(e.target.checked)}
                   data-testid="ack-holds-cannot-bypass" className="mt-0.5" />
            I understand that active Safety Holds cannot be bypassed by unlisting a vehicle, changing
            availability, manually assigning it, or using another ConfirmPH handover route.
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm">Cancel</button>
          <button onClick={enable} disabled={!canEnable} data-testid="confirm-enable-btn"
                  className="px-4 py-2 rounded-lg bg-[#1B4332] text-white text-sm font-medium disabled:opacity-40">
            Enable Enhanced Safety
          </button>
        </div>
      </div>
    </div>
  );
}

function DeactivationRequestForm({ onSubmitted }) {
  const [reasonCode, setReasonCode] = useState("");
  const [explanation, setExplanation] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/enhanced-safety/request-deactivation", {
        reason_code: reasonCode, explanation,
      });
      toast.success("Deactivation request submitted for ConfirmPH review.");
      onSubmitted(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-3" data-testid="deactivation-request-form">
      <h3 className="font-display text-base font-semibold">Request Enhanced Safety Deactivation</h3>
      <p className="text-sm text-slate-600">
        Enhanced Safety Enforcement cannot be disabled instantly because renters and staff may rely on these
        controls. ConfirmPH reviews deactivation requests before the setting can be removed.
      </p>
      <label className="block text-sm">
        <span className="font-medium">Reason</span>
        <select value={reasonCode} onChange={(e) => setReasonCode(e.target.value)}
                data-testid="deactivation-reason-select"
                className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300">
          <option value="">Select a reason...</option>
          {REASON_OPTIONS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
      </label>
      <label className="block text-sm">
        <span className="font-medium">Explanation</span>
        <textarea value={explanation} onChange={(e) => setExplanation(e.target.value)} rows={3}
                  data-testid="deactivation-explanation-input"
                  className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300" />
      </label>
      <button onClick={submit} disabled={busy || !reasonCode || explanation.trim().length < 10}
              data-testid="submit-deactivation-request-btn"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium disabled:opacity-40">
        <Send className="w-4 h-4" /> Submit Request
      </button>
    </div>
  );
}

export default function EnhancedSafetySettings() {
  const { user } = useAuth();
  const isOwner = (user?.account_type || user?.role) === "business_owner";
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showActivate, setShowActivate] = useState(false);
  const [showDeactivateForm, setShowDeactivateForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/enhanced-safety/settings");
      setSettings(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="max-w-3xl text-sm text-slate-500">Loading…</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="enhanced-safety-settings-page">
      <Link to="/app/settings" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="w-4 h-4" /> Back to Settings
      </Link>

      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Vehicles &amp; Safety</div>
        <h1 className="font-display text-3xl font-bold mt-1 inline-flex items-center gap-2">
          <ShieldCheck className="w-6 h-6 text-[#1B4332]" /> Enhanced Vehicle Safety Enforcement
        </h1>
      </div>

      {settings.state === "off" && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 space-y-4" data-testid="enhanced-safety-off-panel">
          <div className="text-sm font-semibold text-slate-500 uppercase tracking-wide">Status: Not Enabled</div>
          <p className="text-sm text-slate-700">
            Enhanced Safety Enforcement adds stronger vehicle controls to ConfirmPH. Serious vehicle issues
            can prevent a vehicle from completing a ConfirmPH handover until the issue is properly resolved.
          </p>
          <div className="flex flex-wrap gap-2">
            {isOwner ? (
              <button onClick={() => setShowActivate(true)} data-testid="enable-enhanced-safety-btn"
                      className="px-4 py-2 rounded-lg bg-[#1B4332] text-white text-sm font-medium">
                Enable Enhanced Safety
              </button>
            ) : (
              <div className="text-xs text-slate-500">Only the business owner can enable this.</div>
            )}
            <Link to="/enhanced-safety" target="_blank"
                  className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium">
              Learn More
            </Link>
          </div>
        </div>
      )}

      {(settings.state === "active" || settings.state === "deactivation_requested") && (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 space-y-4" data-testid="enhanced-safety-active-panel">
          <div className="flex items-center gap-2 text-emerald-900 font-semibold">
            <ShieldCheck className="w-5 h-5" /> ACTIVE
          </div>
          <div className="text-sm text-emerald-900">
            Enabled: {settings.enabled_at ? new Date(settings.enabled_at).toLocaleDateString() : "—"}
          </div>
          <div className="flex items-center gap-2 text-sm text-emerald-900/80">
            <Lock className="w-4 h-4" /> This protection cannot be disabled directly from your business account.
          </div>

          {settings.state === "deactivation_requested" && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900" data-testid="deactivation-under-review-banner">
              <div className="font-semibold">Deactivation Request Under Review</div>
              <div className="mt-1">
                Submitted {new Date(settings.deactivation_request?.requested_at).toLocaleString()}. Existing
                vehicle Safety Holds remain fully active while this is under review.
              </div>
            </div>
          )}

          {settings.state === "active" && settings.deactivation_request?.admin_state === "declined" && (
            <div className="rounded-lg border border-slate-300 bg-white p-4 text-sm text-slate-700" data-testid="deactivation-declined-banner">
              <div className="font-semibold">Deactivation Request Declined</div>
              <div className="mt-1">{settings.deactivation_request.decision_reason}</div>
            </div>
          )}

          {isOwner && settings.state === "active" && !showDeactivateForm && (
            <button onClick={() => setShowDeactivateForm(true)} data-testid="request-deactivation-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-emerald-300 bg-white text-sm font-medium">
              <ShieldAlert className="w-4 h-4" /> Request Deactivation
            </button>
          )}
        </div>
      )}

      {isOwner && settings.state === "active" && showDeactivateForm && (
        <DeactivationRequestForm onSubmitted={(data) => { setSettings(data); setShowDeactivateForm(false); }} />
      )}

      {showActivate && (
        <ActivationModal onClose={() => setShowActivate(false)}
                          onActivated={(data) => { setSettings(data); setShowActivate(false); }} />
      )}
    </div>
  );
}
